import { Chess } from 'chess.js';
import EventEmitter from 'events';

class LocalSocket extends EventEmitter {
  constructor(gameId, user) {
    super();
    // Increase max listeners to prevent warnings
    this.setMaxListeners(30);

    this.gameId = gameId;
    this.user = user;
    this.game = null;
    this.botConfig = null;

    // Slot management properties
    this.whiteSlots = Array(3).fill(null);
    this.blackSlots = Array(3).fill(null);
    this.anarchyMoves = {
      white: null,
      black: null
    };
    this.uniqueMoveLimit = 3;

    // Bot thinking state
    this.isBotThinking = false;

    // Timing controls for a more natural experience
    this.botThinkingTime = {
      min: 50,   // Minimum thinking time in ms
      max: 4000    // Maximum thinking time in ms
    };

    this.slotFillDelays = {
      min: 50,    // Minimum delay between filling slots in ms
      max: 1000    // Maximum delay between filling slots in ms
    };

    this.loadGameState();
  }

  // Load game state from localStorage
  loadGameState() {
    try {
      const savedState = localStorage.getItem(`chess_game_${this.gameId}`);

      if (savedState) {
        this.botConfig = JSON.parse(savedState);
        this.game = new Chess(this.botConfig.fen || undefined);

        // Ensure playerTimes is defined
        if (!this.botConfig.playerTimes) {
          this.botConfig.playerTimes = {
            white: this.botConfig.timeControl || 60,
            black: this.botConfig.timeControl || 60
          };
        }

        // Set isAnarchy property if not already set
        if (this.botConfig.isAnarchy === undefined) {
          this.botConfig.isAnarchy = false;
        }

        // If we have moves, replay them to get current position
        if (this.botConfig.moves && this.botConfig.moves.length > 0) {
          // Reset to initial position first
          this.game = new Chess();

          // Apply each move in sequence
          this.botConfig.moves.forEach(move => {
            try {
              this.game.move(move);
            } catch (error) {
              console.error("Error applying saved move:", move, error);
            }
          });
        }
      } else {
        // Create new game with default settings
        this.game = new Chess();
        this.botConfig = {
          playerColor: 'white',
          status: 'active',
          moves: [],
          fen: this.game.fen(),
          createdBy: this.user.id,
          createdAt: Date.now(),
          timeControl: 60,
          botSlotConfig: {
            slot1: 'random',
            slot2: 'random',
            slot3: 'random'
          },
          isAnarchy: false,
          playerTimes: {
            white: 60,
            black: 60
          }
        };

        // Save the new game state immediately
        this.saveGameState();
      }
    } catch (error) {
      console.error("Error loading game state:", error);
      this.game = new Chess();
      this.botConfig = {
        playerColor: 'white',
        status: 'active',
        moves: [],
        fen: this.game.fen(),
        createdBy: this.user.id,
        createdAt: Date.now(),
        timeControl: 60,
        botSlotConfig: {
          slot1: 'random',
          slot2: 'random',
          slot3: 'random'
        },
        isAnarchy: false,
        playerTimes: {
          white: 60,
          black: 60
        }
      };
      this.saveGameState();
    }
  }

  // Save game state to localStorage
  saveGameState() {
    try {
      // Update FEN in config
      this.botConfig.fen = this.game.fen();

      // Ensure playerTimes is defined before saving
      if (!this.botConfig.playerTimes) {
        this.botConfig.playerTimes = {
          white: this.botConfig.timeControl || 60,
          black: this.botConfig.timeControl || 60
        };
      }

      // Save to localStorage
      localStorage.setItem(`chess_game_${this.gameId}`, JSON.stringify(this.botConfig));
    } catch (error) {
      console.error("Error saving game state:", error);
    }
  }

  // Get a random delay within the specified range
  getRandomDelay(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  // Get thinking time based on game complexity and configuration
  getBotThinkingTime() {
    const isStockfishConfig = this.hasStockfishConfiguration();
    const moveCount = this.game.history().length;
    const isEarlyGame = moveCount < 10;

    // Base thinking time
    let minTime = this.botThinkingTime.min;
    let maxTime = this.botThinkingTime.max;

    // Adjust for stockfish (longer thinking time)
    if (isStockfishConfig) {
      minTime += 1000;
      maxTime += 1500;
    }

    // Adjust for early game (faster responses in opening)
    if (isEarlyGame) {
      minTime = Math.max(500, minTime - 500);
      maxTime = Math.max(1500, maxTime - 500);
    }

    // Return random time within range
    return this.getRandomDelay(minTime, maxTime);
  }

  // Socket interface methods for event management
  on(event, callback) {
    super.on(event, callback);
    return this;
  }

  emit(event, ...args) {
    super.emit(event, ...args);
    return this;
  }

  connect() {
    // Emit connect immediately
    setTimeout(() => {
      this.emit('connect');

      // Then emit game_state after a short delay
      setTimeout(() => {
        this.emit('game_state', this.getGlobalState());

        // Check if bot should make first move
        if (this.shouldBotMove()) {
          // Initialize slots for bot's first turn
          this.initializeSlots();

          // Schedule bot's move after a short delay
          setTimeout(() => {
            this.scheduleBotMove();
          }, 500);
        } else {
          // Initialize slots for player's first turn
          this.initializeSlots();
        }
      }, 100);
    }, 0);

    return this;
  }

  disconnect() {
    this.removeAllListeners();
    return this;
  }

  getGlobalState() {
    // Ensure we have a valid playerColor
    const playerColor = this.botConfig.playerColor || 'white';

    // Ensure playerTimes is defined
    const playerTimes = this.botConfig.playerTimes || {
      white: this.botConfig.timeControl || 60,
      black: this.botConfig.timeControl || 60
    };

    // Get the last move properly
    const lastMove = this.botConfig.moves && this.botConfig.moves.length > 0
      ? this.botConfig.moves[this.botConfig.moves.length - 1]
      : null;

    // Get current state of the game
    return {
      id: this.gameId,
      fen: this.game.fen(),
      turn: this.game.turn(),
      status: this.botConfig.status || 'active',
      players: {
        white: {
          id: playerColor === 'white' ? this.user.id : 'bot',
          username: playerColor === 'white' ? (this.user.username || this.user.id) : 'Bot'
        },
        black: {
          id: playerColor === 'black' ? this.user.id : 'bot',
          username: playerColor === 'black' ? (this.user.username || this.user.id) : 'Bot'
        }
      },
      playerTimes: playerTimes,
      moves: this.botConfig.moves || [],
      isCheck: this.game.isCheck(),
      isAnarchy: this.botConfig.isAnarchy || false,
      lastMove: lastMove,
      timeControl: this.botConfig.timeControl || 60,
      slotPendingMoves: {
        white: this.whiteSlots,
        black: this.blackSlots
      },
      anarchyMoves: this.anarchyMoves,
      isRegularGame: false,
      isLocalGame: true,
      winner: this.botConfig.winner,
      endReason: this.botConfig.endReason,
      uniqueMoveLimit: this.uniqueMoveLimit,
      botSlotConfig: this.botConfig.botSlotConfig
    };
  }
  // Check if it's the bot's turn to move
  shouldBotMove() {
    const botColor = this.botConfig.playerColor === 'white' ? 'black' : 'white';
    return this.game.turn() === (botColor === 'white' ? 'w' : 'b');
  }

  // This method handles commands from the ChessGame component
  emit_socket(event, data) {
    if (event === 'connect_to_game') {
      // When connecting to a game, emit the game state
      setTimeout(() => {
        this.emit('game_state', this.getGlobalState());

        // Check if bot should make first move and initialize slots
        if (this.shouldBotMove()) {
          this.initializeSlots();
          this.scheduleBotMove();
        } else {
          this.initializeSlots();
        }
      }, 100);
    } else if (event === 'make_move') {
      this.makeMove(data.move);
    } else if (event === 'resign') {
      this.handleResign(data.userId);
    } else if (event === 'update_slot_moves') {
      this.updateSlotMoves(data);
    } else if (event === 'reset_slot_moves') {
      this.resetSlotMoves(data);
    } else if (event === 'submit_slot_moves') {
      this.submitSlotMoves(data);
    } else if (event === 'initialize_slot_turn') {
      this.initializeSlots();
    }
  }

  // Initialize slots for current player
  initializeSlots() {
    const currentColor = this.game.turn() === 'w' ? 'white' : 'black';
    const result = this.initializeSlotTurn(currentColor);

    // Emit event to notify of slot initialization
    this.emit('slot_turn_initialized', {
      slotPendingMoves: result.slotPendingMoves,
      anarchyMove: result.anarchyMove,
      uniqueMoveLimit: result.uniqueMoveLimit,
      globalState: this.getGlobalState()
    });
  }

  // Make a regular chess move
  makeMove(move) {
    try {
      // Attempt to make the move
      const result = this.game.move(move);

      if (!result) {
        this.emit('move_error', {
          error: 'Invalid move',
          globalState: this.getGlobalState()
        });
        return false;
      }

      // Ensure playerTimes exists
      if (!this.botConfig.playerTimes) {
        this.botConfig.playerTimes = {
          white: this.botConfig.timeControl || 60,
          black: this.botConfig.timeControl || 60
        };
      }

      // Create a move record
      const moveRecord = {
        ...result,
        timestamp: Date.now(),
        fen: this.game.fen(),
        playerId: this.user.id,
        timeSpent: 0,
        timeRemaining: this.botConfig.playerTimes[result.color === 'w' ? 'white' : 'black']
      };

      // Add move to history
      if (!this.botConfig.moves) {
        this.botConfig.moves = [];
      }
      this.botConfig.moves.push(moveRecord);

      // Save state
      this.saveGameState();

      // Emit game update
      this.emit('game_update', this.getGlobalState());

      // Check for game end conditions
      if (this.checkGameEnd()) {
        return true;
      }

      // If it's the bot's turn, schedule a move
      this.scheduleBotMove();

      return true;
    } catch (error) {
      console.error("Move error:", error);
      this.emit('move_error', {
        error: error.message,
        globalState: this.getGlobalState()
      });
      return false;
    }
  }

  // Check if the game has ended (checkmate, stalemate, etc.)
  checkGameEnd() {
    if (this.game.isCheckmate()) {
      const winner = this.game.turn() === 'w' ? 'black' : 'white';
      this.botConfig.status = 'completed';
      this.botConfig.winner = winner;
      this.botConfig.endReason = 'checkmate';
      this.saveGameState();

      this.emit('game_end', {
        type: 'checkmate',
        winner: winner === 'white'
          ? { id: this.botConfig.playerColor === 'white' ? this.user.id : 'bot', username: winner === this.botConfig.playerColor ? (this.user.username || this.user.id) : 'Bot' }
          : { id: this.botConfig.playerColor === 'black' ? this.user.id : 'bot', username: winner === this.botConfig.playerColor ? (this.user.username || this.user.id) : 'Bot' },
        endReason: 'checkmate',
        globalState: this.getGlobalState()
      });

      return true;
    }

    if (this.game.isDraw()) {
      const reason = this.game.isStalemate() ? 'stalemate' :
        this.game.isInsufficientMaterial() ? 'insufficient material' :
          'draw';

      this.botConfig.status = 'completed';
      this.botConfig.endReason = reason;
      this.saveGameState();

      this.emit('game_end', {
        type: 'draw',
        endReason: reason,
        globalState: this.getGlobalState()
      });

      return true;
    }

    return false;
  }

  // Bot move scheduling
  scheduleBotMove() {
    // Check if it's the bot's turn
    if (!this.shouldBotMove() || this.botConfig.status !== 'active' || this.isBotThinking) {
      return;
    }

    // Mark bot as thinking
    this.isBotThinking = true;

    // Determine bot color
    const botColor = this.botConfig.playerColor === 'white' ? 'black' : 'white';

    // Minimal thinking delay (200ms)
    setTimeout(() => {
      this.fillBotSlotsWithDelay(botColor);
    }, 500);
  }

  // Fill bot slots with delay to simulate human-like selection
  fillBotSlotsWithDelay(color) {
    // Reset slots first
    if (color === 'white') {
      this.whiteSlots = Array(3).fill(null);
    } else {
      this.blackSlots = Array(3).fill(null);
    }

    // Generate legal moves for current position
    const legalMoves = this.game.moves({ verbose: true });

    // Select moves based on configuration
    let movesToUse = this.selectBotMoves(legalMoves, color);

    // Special case for anarchy mode
    if (this.botConfig.isAnarchy) {
      // Anarchy move is always in slot 3
      const anarchyMove = movesToUse.pop(); // Take the last move for slot 3

      // Add all moves in sequence with minimal delay
      this.addBotMove(color, 2, anarchyMove);

      // Add the other moves with minimal delays
      setTimeout(() => {
        if (movesToUse.length > 0) {
          this.addBotMove(color, 0, movesToUse[0]);
        }

        setTimeout(() => {
          if (movesToUse.length > 1) {
            this.addBotMove(color, 1, movesToUse[1]);
          }

          // Submit moves after a consistent delay
          setTimeout(() => {
            this.submitSlotMoves({ color });
          }, 200);
        }, 200);
      }, 200);
    } else {
      // Regular mode - fill slots with minimal delays
      if (movesToUse.length > 0) this.addBotMove(color, 0, movesToUse[0]);

      setTimeout(() => {
        if (movesToUse.length > 1) this.addBotMove(color, 1, movesToUse[1]);

        setTimeout(() => {
          if (movesToUse.length > 2) this.addBotMove(color, 2, movesToUse[2]);

          // Submit moves after all slots are filled
          setTimeout(() => {
            this.submitSlotMoves({ color });
          }, 200);
        }, 200);
      }, 200);
    }
  }

  // Check if bot is using Stockfish configuration
  hasStockfishConfiguration() {
    const config = this.botConfig.botSlotConfig || {};
    return (
      config.slot1 === '1' || config.slot1 === '2' || config.slot1 === '3' ||
      config.slot2 === '1' || config.slot2 === '2' || config.slot2 === '3' ||
      config.slot3 === '1' || config.slot3 === '2' || config.slot3 === '3'
    );
  }

  // Select moves for bot based on configuration
  selectBotMoves(legalMoves, color) {
    if (!legalMoves || legalMoves.length === 0) return [];

    // Determine which moves to use based on bot configuration
    let movesToUse = [];
    const config = this.botConfig.botSlotConfig || {};

    // In anarchy mode, first handle potential en passant move for slot 3
    if (this.botConfig.isAnarchy) {
      const enPassantMoves = legalMoves.filter(move => {
        const flags = move.flags || '';
        return flags.includes('e');
      });

      // If we have en passant moves, use one for slot 3
      const slot3Move = enPassantMoves.length > 0
        ? this.selectRandomMove(enPassantMoves)
        : this.selectRandomMove(legalMoves);

      movesToUse.push(this.formatMove(slot3Move));

      // Filter out the slot 3 move to avoid duplicates
      const remainingMoves = legalMoves.filter(move =>
        move.san !== slot3Move.san
      );

      // Add moves for slots 1 and 2
      if (remainingMoves.length > 0) {
        movesToUse.unshift(this.formatMove(this.selectRandomMove(remainingMoves)));

        if (remainingMoves.length > 1) {
          // Filter out the move we just added
          const lastMove = movesToUse[0];
          const finalRemainingMoves = remainingMoves.filter(move =>
            move.san !== lastMove.san
          );

          if (finalRemainingMoves.length > 0) {
            movesToUse.unshift(this.formatMove(this.selectRandomMove(finalRemainingMoves)));
          }
        }
      }
    } else {
      // For regular mode, select random moves
      const moveCount = Math.min(3, legalMoves.length);
      let availableMoves = [...legalMoves];

      for (let i = 0; i < moveCount; i++) {
        const randomIndex = Math.floor(Math.random() * availableMoves.length);
        const move = availableMoves.splice(randomIndex, 1)[0];
        movesToUse.push(this.formatMove(move));
      }
    }

    return movesToUse;
  }

  // Format a move object
  formatMove(move) {
    if (!move) return null;

    // Use optional chaining to handle potential undefined values
    const piece = this.game.get(move.from);

    return {
      ...move,
      color: this.game.turn(),
      piece: piece?.type || 'p', // Default to pawn if piece type is undefined
      san: move.san
    };
  }

  // Add a move to a specific bot slot with UI update
  addBotMove(color, slotIndex, move) {
    if (!move) return;

    // Update the appropriate slot
    if (color === 'white') {
      const newSlots = [...this.whiteSlots];
      newSlots[slotIndex] = move;
      this.whiteSlots = newSlots;
      if (this.botConfig.isAnarchy && slotIndex === 2) {
        this.anarchyMoves.white = move;
      }
    } else {
      const newSlots = [...this.blackSlots];
      newSlots[slotIndex] = move;
      this.blackSlots = newSlots;
      if (this.botConfig.isAnarchy && slotIndex === 2) {
        this.anarchyMoves.black = move;
      }
    }

    // Emit update to show the move being added
    this.emit('slot_moves_updated', {
      slotPendingMoves: color === 'white' ? this.whiteSlots : this.blackSlots,
      color,
      anarchyMove: this.botConfig.isAnarchy ? this.anarchyMoves[color] : null,
      globalState: this.getGlobalState()
    });
  }

  // Slot management
  initializeSlotTurn(color) {
    // Reset slots for specified color
    if (color === 'white') {
      this.whiteSlots = Array(3).fill(null);
    } else {
      this.blackSlots = Array(3).fill(null);
    }

    // Generate legal moves for current position
    const legalMoves = this.game.moves({ verbose: true });

    // Determine uniqueMoveLimit
    const uniqueMoveLimit = Math.min(legalMoves.length, 3);
    this.uniqueMoveLimit = uniqueMoveLimit;

    // Check if this is a bot color
    const isBotColor = (color === 'white' && this.botConfig.playerColor === 'black') ||
      (color === 'black' && this.botConfig.playerColor === 'white');

    // Always populate slots for human player if there are fewer legal moves than slots (forced choice)
    // For bot color, we'll let fillBotSlotsWithDelay handle it
    const isForcedChoice = legalMoves.length <= 3;
    const shouldPopulateSlots = !isBotColor && isForcedChoice;

    // Initialize slots array
    let filledSlots = Array(3).fill(null);

    if (shouldPopulateSlots) {
      // This is for the human player with forced choices
      // Special handling for anarchy mode
      if (this.botConfig.isAnarchy) {
        // Check for en passant moves first
        const enPassantMoves = legalMoves.filter(move => {
          const flags = move.flags || '';
          return flags.includes('e');
        });

        if (enPassantMoves.length > 0) {
          // In anarchy mode, en passant is in slot 3
          const enPassantMove = this.selectRandomMove(enPassantMoves);
          this.anarchyMoves[color] = {
            ...enPassantMove,
            color: this.game.turn(),
            piece: this.game.get(enPassantMove.from)?.type || 'p',
            san: enPassantMove.san
          };
          filledSlots[2] = this.anarchyMoves[color];
        } else {
          // No en passant, just pick a random move for slot 3
          const randomMove = this.selectRandomMove(legalMoves);
          this.anarchyMoves[color] = {
            ...randomMove,
            color: this.game.turn(),
            piece: this.game.get(randomMove.from)?.type || 'p',
            san: randomMove.san
          };
          filledSlots[2] = this.anarchyMoves[color];
        }

        // Now fill slots 1 and 2 with random moves (not the same as slot 3)
        const remainingMoves = legalMoves.filter(move =>
          !filledSlots.some(slot => slot && slot.san === move.san)
        );

        // Randomly select moves for slots 0 and 1
        if (remainingMoves.length > 0) {
          for (let i = 0; i < Math.min(2, remainingMoves.length); i++) {
            const moveIndex = Math.floor(Math.random() * remainingMoves.length);
            const selectedMove = remainingMoves.splice(moveIndex, 1)[0];

            filledSlots[i] = {
              ...selectedMove,
              color: this.game.turn(),
              piece: this.game.get(selectedMove.from)?.type || 'p',
              san: selectedMove.san
            };
          }
        }
      } else {
        // Regular mode - just fill available slots with random moves
        let availableMoves = [...legalMoves];

        for (let i = 0; i < Math.min(3, availableMoves.length); i++) {
          const moveIndex = Math.floor(Math.random() * availableMoves.length);
          const selectedMove = availableMoves.splice(moveIndex, 1)[0];

          filledSlots[i] = {
            ...selectedMove,
            color: this.game.turn(),
            piece: this.game.get(selectedMove.from)?.type || 'p',
            san: selectedMove.san
          };
        }
      }
    }

    // Set the filled slots
    if (color === 'white') {
      this.whiteSlots = filledSlots;
    } else {
      this.blackSlots = filledSlots;
    }

    return {
      slotPendingMoves: filledSlots,
      anarchyMove: this.botConfig.isAnarchy ? filledSlots[2] : null,
      uniqueMoveLimit: uniqueMoveLimit,
      globalState: this.getGlobalState()
    };
  }

  // Helper to select a random move
  selectRandomMove(moves) {
    if (!moves || moves.length === 0) return null;
    return moves[Math.floor(Math.random() * moves.length)];
  }

  // Slot move submission
  submitSlotMoves({ color }) {
    try {
      // Get the slots for the specified color
      const slots = color === 'white' ? this.whiteSlots : this.blackSlots;

      // Filter out null slots
      const availableSlots = slots.filter(slot => slot !== null);

      if (availableSlots.length === 0) {
        console.error('No available moves in slots');
        this.isBotThinking = false;
        return false;
      }

      // Special handling for en passant in anarchy mode (matching server behavior)
      let selectedMove;
      if (this.botConfig.isAnarchy) {
        // Check for en passant moves first in anarchy mode
        const enPassantMoves = availableSlots.filter(move => {
          return move.flags?.includes("e");
        });

        if (enPassantMoves.length > 0) {
          // If there's an en passant move, use it (following server behavior)
          selectedMove = this.selectRandomMove(enPassantMoves);
        } else {
          // Otherwise pick a random legal move from available slots
          selectedMove = this.selectRandomMove(availableSlots);
        }
      } else {
        // Regular mode - just pick a random move from available slots
        selectedMove = this.selectRandomMove(availableSlots);
      }

      // Signal spin animation start
      this.emit('start_slot_spin', {
        selectedMove,
        color,
        availableMoves: availableSlots
      });

      // Wait for animation time, then make the move
      setTimeout(() => {
        try {
          // Make the move on the chess board
          const moveResult = this.game.move({
            from: selectedMove.from,
            to: selectedMove.to,
            promotion: selectedMove.promotion
          });

          if (moveResult) {
            // Create a move record with metadata
            const moveRecord = {
              ...moveResult,
              timestamp: Date.now(),
              fen: this.game.fen(),
              playerId: color === this.botConfig.playerColor ? this.user.id : 'bot',
              timeSpent: 0,
              timeRemaining: this.botConfig.playerTimes[moveResult.color === 'w' ? 'white' : 'black'],
              slotMoves: availableSlots.map(slot => ({
                from: slot.from,
                to: slot.to,
                piece: slot.piece,
                promotion: slot.promotion,
                san: slot.san,
                color: slot.color
              }))
            };

            // Add move to history
            if (!this.botConfig.moves) {
              this.botConfig.moves = [];
            }
            this.botConfig.moves.push(moveRecord);

            // Save state
            this.saveGameState();

            // Check for game end
            if (this.checkGameEnd()) {
              this.isBotThinking = false;
              return true;
            }

            // Reset slots
            if (color === 'white') {
              this.whiteSlots = Array(3).fill(null);
            } else {
              this.blackSlots = Array(3).fill(null);
            }

            // Initiate new turn - IMPORTANT: This is where we emit game_update
            // Make sure to send a complete and updated state
            const completeUpdatedState = this.getGlobalState();

            // First send game_update with complete move information
            this.emit('game_update', completeUpdatedState);

            // Then after a small delay, initialize the slots for the next turn
            setTimeout(() => {
              const nextColor = this.game.turn() === 'w' ? 'white' : 'black';
              const result = this.initializeSlotTurn(nextColor);

              // Emit slot turn initialized event with the same up-to-date state
              this.emit('slot_turn_initialized', {
                slotPendingMoves: result.slotPendingMoves,
                anarchyMove: result.anarchyMove,
                uniqueMoveLimit: result.uniqueMoveLimit,
                globalState: completeUpdatedState  // Use the same state object for consistency
              });

              // If it's still the bot's turn, schedule next move
              this.isBotThinking = false;
              this.scheduleBotMove();
            }, 500);
          } else {
            // Move failed, reset bot thinking state
            this.isBotThinking = false;
            console.error('Bot made invalid move');
          }
        } catch (error) {
          console.error('Error making bot move:', error);
          this.isBotThinking = false;
        }
      }, 800); // Slot spin animation time

      return true;
    } catch (error) {
      console.error('Error in submitSlotMoves:', error);
      this.isBotThinking = false;
      return false;
    }
  }

  // Update slot moves
  updateSlotMoves({ moves, color }) {
    try {
      // Preserve anarchy move if needed
      if (this.botConfig.isAnarchy && color) {
        // Ensure the anarchy move is preserved in slot 3
        const anarchyMove = this.anarchyMoves[color] || moves[2];
        if (anarchyMove) {
          moves[2] = anarchyMove;
          this.anarchyMoves[color] = anarchyMove;
        }
      }

      // Store the slot moves in the appropriate color array
      if (color === 'white') {
        this.whiteSlots = [...moves];
      } else {
        this.blackSlots = [...moves];
      }

      // Emit event to update UI with new slot state
      this.emit('slot_moves_updated', {
        slotPendingMoves: moves,
        color,
        anarchyMove: this.anarchyMoves[color],
        globalState: this.getGlobalState()
      });

      return true;
    } catch (error) {
      console.error('Error updating slot moves:', error);
      return false;
    }
  }

  // Reset slot moves
  resetSlotMoves({ color, anarchyMove }) {
    try {
      // Reset slots for the specified color
      const resetSlots = Array(3).fill(null);

      // Preserve anarchy move if applicable
      if (this.botConfig.isAnarchy && anarchyMove) {
        resetSlots[2] = anarchyMove;
        this.anarchyMoves[color] = anarchyMove;
      }

      // Update the appropriate slot array
      if (color === 'white') {
        this.whiteSlots = resetSlots;
      } else {
        this.blackSlots = resetSlots;
      }

      // Emit event to update UI
      this.emit('slot_moves_reset', {
        color,
        slotPendingMoves: resetSlots,
        anarchyMove: this.anarchyMoves[color],
        globalState: this.getGlobalState()
      });

      return true;
    } catch (error) {
      console.error('Error resetting slot moves:', error);
      return false;
    }
  }

  // Handle player resignation
  handleResign(userId) {
    try {
      // Determine the winner (opposite of resigning player)
      const winner = userId === this.user.id ? (
        this.botConfig.playerColor === 'white' ? 'black' : 'white'
      ) : (
        this.botConfig.playerColor === 'white' ? 'white' : 'black'
      );

      // Update game state
      this.botConfig.status = 'completed';
      this.botConfig.winner = winner;
      this.botConfig.endReason = 'resignation';
      this.saveGameState();

      // Emit game end event
      this.emit('game_end', {
        type: 'resignation',
        winner: winner === 'white'
          ? { id: this.botConfig.playerColor === 'white' ? this.user.id : 'bot', username: winner === this.botConfig.playerColor ? (this.user.username || this.user.id) : 'Bot' }
          : { id: this.botConfig.playerColor === 'black' ? this.user.id : 'bot', username: winner === this.botConfig.playerColor ? (this.user.username || this.user.id) : 'Bot' },
        endReason: 'resignation',
        globalState: this.getGlobalState()
      });

      return true;
    } catch (error) {
      console.error('Error handling resignation:', error);
      return false;
    }
  }
}

export default LocalSocket;